import { Component, OnInit, OnDestroy, Input, ElementRef, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SEOService } from 'src/app/services/seo.service';
import { Currency } from 'src/app/models/currency';
import { CurrenciesService } from 'src/app/api/currencies.service';
import { CryptoPriceData, PriceChartDuration } from 'src/app/models/price-chart';
import { CryptoPriceService } from 'src/app/services/crypto-price.service';
import { environment } from 'src/environments/environment';
import { CoinInfoService } from 'src/app/api/coin-info.service';
import { CoinInfo, TokenRatingType, CoinRating, CoinRatingBadge } from 'src/app/models/coin-info';
import { CurrencyDetailService } from 'src/app/api/currency-detail.service';
import { ThemeService } from 'src/app/services/theme.service';
import { Theme } from 'src/app/models/theme';
import { RoundNumberPricePipe } from 'src/app/pipes/round-number-price.pipe';
import { map, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'crypto-detail',
  templateUrl: './crypto-page-detail.component.html',
  styleUrls: ['./crypto-page-detail.component.scss'],
  providers: [CryptoPriceService, RoundNumberPricePipe]
})
export class CryptoPageDetailComponent implements OnInit, OnDestroy {
  @Input() fullLayout: boolean;
  PriceChartDuration = PriceChartDuration;
  chartColor: string;
  pair: string;
  mainCurrency: string;
  baseCurrency: string;

  currency: Currency;
  baseCurrencyInfo:Currency;
  currencies: Currency[];
  duration = PriceChartDuration.SevenDays;
  coinInfo: CoinInfo;
  coinRating: CoinRating;
  accentWidth: string;
  TokenRatingType = TokenRatingType;
  CoinRatingBadge = CoinRatingBadge;
  showTokenInsightInfoModal = false;
  showTokenInsightDetailsModal = false;

  private currencyDetailACF: { tokenPriceToday: string; howToBuy: string; whyTrade: string; faqs: string; };
  currencyDetailTokenPriceToday: string;
  currencyDetailHowToBuy: string;
  currencyDetailWhyTrade: string;
  currencyDetailFaqs: string;

  loading = false;
  error: string = null;

  data: CryptoPriceData;

  globalCurrency = environment.currencies.globalCurrency;

  useGlobalCurrency = !!this.globalCurrency;
  showGlobalCurrency = !!this.globalCurrency;

  theme = Theme.Light;

  theme$ = this.themeService.themeChanges.pipe(map(it => it));

  private destroy$ = new Subject<void>();
  private scrollObserver: IntersectionObserver;

  constructor(
    private el: ElementRef,
    private route: ActivatedRoute,
    private router: Router,
    private currenciesService: CurrenciesService,
    private cryptoPriceService: CryptoPriceService,
    private coinInfoService: CoinInfoService,
    private themeService: ThemeService,
    private seoService: SEOService,
    private renderer: Renderer2,
    private currencyDetailService: CurrencyDetailService,
    private roundNumberPricePipe: RoundNumberPricePipe,
  ) {}

  ngOnInit() {
    if (this.fullLayout) {
      this.themeService.set(Theme.Light, false);
    }

    this.cryptoPriceService.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(val => {
      this.data = val;
      this.setAccentWidth();
      this.updateCurrencyDetailContent();
    });

    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.pair = params.get('pair');
      if (!this.pair) {
        this.error = 'No Crypto Code provided';
      } else {
        const currencies = this?.pair?.split('-');
        this.mainCurrency = currencies[0];
        this.baseCurrency = currencies[1];

        this.cryptoPriceService.setBaseCurrency(this.baseCurrency);

        this.getCoinInfo();
        this.getCoinRating();
        this.getCurrency();
        this.setAccentWidth();
      }
    });
  }

  setAccentWidth() {
    const lastPrice = this.data.livePriceHistory?.baseCurrencyData?.data?.[0].y;
    const rangePrice = lastPrice - this.data.livePriceHistory?.baseCurrencyLimit?.min;
    const limit = this.data.livePriceHistory?.baseCurrencyLimit?.max - this.data.livePriceHistory?.baseCurrencyLimit?.min;
    this.accentWidth = `${(rangePrice / limit) * 100}%`;
  }

  getCurrency() {
    this.loading = true;
    this.error = null;
    this.currenciesService.getCryptoWithSupportedBase(this.baseCurrency).subscribe({
      next: (supported) => {        
        this.loading = false;

        this.currency = supported.find((c: Currency) => c.code === this.mainCurrency);

        this.currenciesService.getFiat().subscribe((res) => {
          this.baseCurrencyInfo = res.find((c: Currency) => c.code === this.baseCurrency);
        });

        this.currencies = supported.filter((c: Currency) => c.code !== this.mainCurrency);

        if (!this.currency) {
          this.seoService.addNoIndex();
          this.router.navigate(['/not-found']);
          return;
        }

        const name = this.currency.name;
        this.seoService.addFinancialProductSchema(this.renderer, name, this.pair);
        this.seoService.addBreadcrumbSchema(this.renderer, name, this.pair);
        this.cryptoPriceService.setCurrency(this.mainCurrency);
        this.cryptoPriceService.setDuration(this.duration);
        this.getCurrencyDetail();
        this.setupScrollAnimations();
      },
      error: () => {
        this.loading = false;
        this.error = 'No Crypto provided';
      }
    });
  }

  getCoinInfo() {
    this.coinInfoService.getCoinInfo(this.mainCurrency).subscribe(coinInfo => {
      this.coinInfo = coinInfo?.[0];
      this.setupScrollAnimations();
    }, err => {
      this.loading = false;
    });
  }

  getCoinRating() {
    this.coinInfoService.getCoinRating(this.mainCurrency).subscribe(coinRating => {
      this.coinRating = coinRating?.[0];
      if(this.coinRating)
        this.coinRating.badge = this.getScoreBadge(this.coinRating?.score);
      this.setupScrollAnimations();
    }, err => {
      this.loading = false;
    });
  }

  getCurrencyDetail() {
    this.currencyDetailService.getCurrencyDetail(this.mainCurrency).subscribe(acf => {
      if (!acf) return;
      this.currencyDetailACF = {
        tokenPriceToday: acf.price_details_token_price_today,
        howToBuy: acf.price_details_how_to_buy,
        whyTrade: acf.price_details_why_trade,
        faqs: acf.price_detail_faqs,
      };
      this.updateCurrencyDetailContent();
      this.setupScrollAnimations();
    }, () => {});
  }

  private updateCurrencyDetailContent(): void {
    if (!this.currencyDetailACF) return;
    this.currencyDetailTokenPriceToday = this.replacePlaceholders(this.currencyDetailACF.tokenPriceToday);
    this.currencyDetailHowToBuy = this.replacePlaceholders(this.currencyDetailACF.howToBuy);
    this.currencyDetailWhyTrade = this.replacePlaceholders(this.currencyDetailACF.whyTrade);
    this.currencyDetailFaqs = this.replacePlaceholders(this.currencyDetailACF.faqs);
  }

  private replacePlaceholders(content: string): string {
    if (!content) return null;
    const price = this.data?.baseCurrency?.lastPrice
      ? this.roundNumberPricePipe.transform(this.data.baseCurrency.lastPrice)
      : '';
    return content
      .replace(/\r\n/g, '<br>')
      .replace(/\{name\}/g, this.currency?.name || '')
      .replace(/\{code\}/g, this.mainCurrency || '')
      .replace(/\{fiat_code\}/g, this.baseCurrency || '')
      .replace(/\{price\}/g, price);
  }

  changeDuration(duration: PriceChartDuration) {
    if (duration === this.duration) {
      return;
    }

    this.duration = duration;
    this.cryptoPriceService.setDuration(this.duration);
  }

  getScoreBadge (ratingScore: String) {
    const score = parseFloat((ratingScore || '0').toString());

    if (score < 33.3)
        return CoinRatingBadge.BAD;
      else if (score >= 33.3 && score < 66.6)
        return  CoinRatingBadge.OK;
      else
        return CoinRatingBadge.GOOD;
  }

  onTokenInsightInfoClick(event : Event) {
    this.showTokenInsightInfoModal = true;
    return false;
  }

  onTokenInsightInfoClose() {
    this.showTokenInsightInfoModal = false;
  }

  onTokenInsightDetailsClick(event : Event) {
    this.showTokenInsightDetailsModal = true;
    return false;
  }

  onTokenInsightDetailsClose() {
    this.showTokenInsightDetailsModal = false;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.scrollObserver?.disconnect();
  }

  private setupScrollAnimations() {
    if (!this.fullLayout) return;

    // Use setTimeout to ensure Angular change detection has rendered new elements
    setTimeout(() => {
      const root = this.el.nativeElement as HTMLElement;
      const targets = root.querySelectorAll('.scroll-reveal:not(.in-view)');
      if (!targets.length) return;

      if (!this.scrollObserver) {
        this.scrollObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('in-view');
              this.scrollObserver.unobserve(entry.target);
            }
          });
        }, { threshold: 0.1 });
      }

      targets.forEach(el => this.scrollObserver.observe(el));
    });
  }

}
